import React, { useState } from 'react';
import { 
  Building, ShieldCheck, Cpu, HardDrive, Database, Settings, Plus, UserPlus, Check, X, Shield, Lock, Zap, CheckCircle2, AlertTriangle, Grid, Server, Activity, DollarSign
} from 'lucide-react';
import { ModuleExplorer } from './ModuleExplorer';

export interface TenantAdmin {
  id: string;
  adminName: string;
  adminEmail: string;
  schoolName: string;
  slug: string;
  plan: string;
  modules: string[];
  studentCount: number;
  subscriptionStatus: 'ACTIVE' | 'EXPIRED' | 'PENDING_ACTIVATION';
  subscriptionExpiry: string;
}

const ALL_30_MODULES = [
  { key: 'aac', label: '1. AAC Speech Synthesizer' },
  { key: 'speech-therapy', label: '2. Speech & Articulation' },
  { key: 'autism', label: '3. Autism & ABA Behavior' },
  { key: 'sign-language', label: '4. Sign Language & Gestures' },
  { key: 'braille', label: '5. Braille & Tactile Learning' },
  { key: 'student', label: '6. Student & IEP Progress' },
  { key: 'therapist', label: '7. Therapist Workspace & SOAP' },
  { key: 'teacher', label: '8. Teacher & Classroom' },
  { key: 'assessment', label: '9. Diagnostic Assessment' },
  { key: 'school-erp', label: '10. Special School ERP' },
  { key: 'hospital', label: '11. Rehabilitation Hospital' },
  { key: 'billing', label: '12. NDIS & Insurance Billing' },
  { key: 'communication', label: '13. Parent Communication' },
  { key: 'inventory', label: '14. Assistive Device Inventory' },
  { key: 'analytics', label: '15. Executive Analytics BI' },
  { key: 'ai', label: '16. AI Copilot Summarizer' },
  { key: 'sensory-room', label: '17. Sensory Room Controller' },
  { key: 'tele-rehab', label: '18. Tele-Rehabilitation Video' },
  { key: 'early-intervention', label: '19. Early Intervention Milestone' },
  { key: 'vocational-living', label: '20. Vocational & Life Skills' },
  { key: 'hydrotherapy', label: '21. Hydrotherapy Aquatic Rehab' },
  { key: 'music-therapy', label: '22. Music Auditory Stimulation' },
  { key: 'animal-therapy', label: '23. Animal-Assisted Therapy' },
  { key: 'prosthetics', label: '24. Bionic Prosthetics & Exoskeleton' },
  { key: 'vbmapp-assessment', label: '25. VB-MAPP Assessment Protocol' },
  { key: 'core-vocab-ai', label: '26. Motor Core Vocab & AI' },
  { key: 'fim-weefim', label: '27. FIM Functional Scoring' },
  { key: 'evv-gps-tracker', label: '28. EVV Visit Verification & GPS' },
  { key: 'gamified-neuro', label: '29. WebGL Biofeedback Neuro-Rehab' },
  { key: 'transition-matrix', label: '30. Transition & Vocational Matrix' }
];

const ALL_MODULE_KEYS = ALL_30_MODULES.map(m => m.key);

const INITIAL_TENANT_ADMINS: TenantAdmin[] = [
  {
    id: 'demo-user',
    adminName: 'Demo Admin User',
    adminEmail: 'demo@bugfreesolution.com',
    schoolName: 'BugFree Solutions Demo Organization',
    slug: 'bugfree-demo',
    plan: 'ENTERPRISE_FULL_SUITE',
    studentCount: 175,
    subscriptionStatus: 'ACTIVE',
    subscriptionExpiry: '2028-12-31',
    modules: ALL_MODULE_KEYS
  },
  { 
    id: '1', 
    adminName: 'Dr. Robert Vance', 
    adminEmail: 'principal@stjude.edu', 
    schoolName: 'St. Jude Special School', 
    slug: 'stjude', 
    plan: 'ENTERPRISE_SCHOOL', 
    studentCount: 142,
    subscriptionStatus: 'ACTIVE',
    subscriptionExpiry: '2027-08-31',
    modules: ALL_MODULE_KEYS
  },
  { 
    id: '2', 
    adminName: 'Dr. Ananya Sharma', 
    adminEmail: 'admin@sunshinetherapy.org', 
    schoolName: 'Sunshine Therapy Centre', 
    slug: 'sunshine', 
    plan: 'PRO_CLINIC', 
    studentCount: 88,
    subscriptionStatus: 'ACTIVE',
    subscriptionExpiry: '2027-04-15',
    modules: ['aac', 'speech-therapy', 'therapist', 'billing', 'communication', 'early-intervention', 'tele-rehab'] 
  },
  { 
    id: '3', 
    adminName: 'Superintendent K. Patil', 
    adminEmail: 'director@districtrehab.gov', 
    schoolName: 'District Rehab Department', 
    slug: 'rehab', 
    plan: 'GOVT_REHAB', 
    studentCount: 350,
    subscriptionStatus: 'EXPIRED',
    subscriptionExpiry: '2026-06-01',
    modules: ALL_MODULE_KEYS 
  }
];

export const SuperAdminDashboard: React.FC = () => {
  const [tenantAdmins, setTenantAdmins] = useState<TenantAdmin[]>(INITIAL_TENANT_ADMINS);
  const [activeTab, setActiveTab] = useState<'licensing' | 'modules_explorer' | 'server_health'>('licensing');
  const [isAddAdminOpen, setIsAddAdminOpen] = useState(false);

  // New Admin Form State
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [plan, setPlan] = useState('ENTERPRISE_FULL_SUITE');
  const [selectedModules, setSelectedModules] = useState<string[]>(ALL_MODULE_KEYS);
  const [successMsg, setSuccessMsg] = useState('');

  const toggleModuleForAdmin = (adminId: string, moduleKey: string) => {
    setTenantAdmins((prev) =>
      prev.map((item) => {
        if (item.id !== adminId) return item;
        const exists = item.modules.includes(moduleKey);
        const updated = exists
          ? item.modules.filter((m) => m !== moduleKey)
          : [...item.modules, moduleKey];
        return { ...item, modules: updated };
      })
    );
  };

  const grantAllModulesToAdmin = (adminId: string) => {
    setTenantAdmins((prev) =>
      prev.map((item) => {
        if (item.id !== adminId) return item;
        return { ...item, modules: ALL_MODULE_KEYS, subscriptionStatus: 'ACTIVE' };
      })
    );
    setSuccessMsg('Granted ALL 30 Modules & activated subscription!');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const toggleSubscriptionStatus = (adminId: string) => {
    setTenantAdmins((prev) =>
      prev.map((item) => {
        if (item.id !== adminId) return item;
        const newStatus = item.subscriptionStatus === 'ACTIVE' ? 'EXPIRED' : 'ACTIVE';
        return { 
          ...item, 
          subscriptionStatus: newStatus,
          subscriptionExpiry: newStatus === 'ACTIVE' ? '2028-12-31' : 'Expired (Action Required)'
        };
      })
    );
    setSuccessMsg('Updated tenant subscription status successfully!');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleCreateTenantAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminName || !adminEmail || !schoolName) return;

    const slug = schoolName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const newAdmin: TenantAdmin = {
      id: Date.now().toString(),
      adminName,
      adminEmail,
      schoolName,
      slug,
      plan,
      studentCount: 0,
      subscriptionStatus: 'ACTIVE',
      subscriptionExpiry: '2028-12-31',
      modules: selectedModules
    };

    setTenantAdmins([newAdmin, ...tenantAdmins]);
    setSuccessMsg(`Tenant Admin "${adminName}" created & active subscription granted for ${schoolName}!`);
    setAdminName('');
    setAdminEmail('');
    setSchoolName('');
    setIsAddAdminOpen(false);

    setTimeout(() => setSuccessMsg(''), 4000);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pt-28 pb-16 px-4">
      
      {/* Clarvos Super Admin Control Plane Header */}
      <div className="bg-[#1c1b1f] text-white p-7 sm:p-8 rounded-[32px] shadow-2xl border border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden backdrop-blur-xl">
        <div className="space-y-3 z-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-[#ffd64e]/10 text-[#ffd64e] text-xs font-black rounded-full uppercase tracking-wider border border-[#ffd64e]/30">
            <Shield className="w-3.5 h-3.5 text-[#ffd64e]" />
            <span>Super Admin Plane • WHM Root Controller</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-white">Global Multi-Tenant Licensing & Server Control</h2>
          <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
            Manage 1,400+ Special Schools, provision tenant administrators, grant subscriptions, and govern all 30 AbilityOS clinical modules across deployments.
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0 z-10">
          <button
            onClick={() => setIsAddAdminOpen(true)}
            className="px-6 py-3 bg-[#ffd64e] hover:bg-[#ffcf33] text-[#1c1b1f] font-black rounded-full text-xs flex items-center gap-2 shadow-md transition scale-[1.02]"
          >
            <UserPlus className="w-4 h-4 text-[#1c1b1f]" />
            <span>Add Tenant Admin</span>
          </button>
        </div>
      </div>

      {/* Clarvos Navigation Tabs */}
      <div className="bg-[#1c1b1f] p-2 rounded-[28px] border border-white/10 shadow-lg flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setActiveTab('licensing')}
            className={`px-5 py-2.5 rounded-full font-black text-xs transition-all flex items-center gap-2 ${
              activeTab === 'licensing' 
                ? 'bg-[#ffd64e] text-[#1c1b1f] shadow-md scale-[1.02]' 
                : 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/5'
            }`}
          >
            <ShieldCheck className="w-4 h-4 text-current" />
            <span>Tenant Licensing & Subscription Matrix</span>
          </button>

          <button
            onClick={() => setActiveTab('modules_explorer')}
            className={`px-5 py-2.5 rounded-full font-black text-xs transition-all flex items-center gap-2 ${
              activeTab === 'modules_explorer' 
                ? 'bg-[#ffd64e] text-[#1c1b1f] shadow-md scale-[1.02]' 
                : 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/5'
            }`}
          >
            <Grid className="w-4 h-4 text-current" />
            <span>🚀 30-Module Suite Launcher</span>
          </button>

          <button
            onClick={() => setActiveTab('server_health')}
            className={`px-5 py-2.5 rounded-full font-black text-xs transition-all flex items-center gap-2 ${
              activeTab === 'server_health' 
                ? 'bg-[#ffd64e] text-[#1c1b1f] shadow-md scale-[1.02]' 
                : 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/5'
            }`}
          >
            <Server className="w-4 h-4 text-current" />
            <span>cPanel MultiPHP 8.5 Server Health</span>
          </button>
        </div>
      </div>

      {successMsg && (
        <div className="p-4 bg-[#bfecc7]/20 border border-[#bfecc7]/50 text-[#1c1b1f] rounded-[20px] text-xs font-bold flex items-center gap-2 shadow-sm">
          <Check className="w-4 h-4 text-emerald-700" />
          {successMsg}
        </div>
      )}

      {/* Global System Key Metrics Bento Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-5 bg-[#f9f6f0] border border-black/10 rounded-[28px] shadow-sm flex items-center gap-4">
          <div className="p-3 bg-[#1c1b1f] text-[#ffd64e] rounded-[20px]"><Building className="w-5 h-5" /></div>
          <div>
            <div className="text-2xl font-black text-[#1c1b1f] font-mono">{tenantAdmins.length}</div>
            <div className="text-[11px] text-[#1c1b1f]/60 font-bold uppercase tracking-wider">Tenant Admins</div>
          </div>
        </div>

        <div className="p-5 bg-[#f9f6f0] border border-black/10 rounded-[28px] shadow-sm flex items-center gap-4">
          <div className="p-3 bg-[#1c1b1f] text-[#bfecc7] rounded-[20px]"><Zap className="w-5 h-5" /></div>
          <div>
            <div className="text-2xl font-black text-[#1c1b1f] font-mono">
              {tenantAdmins.filter(t => t.subscriptionStatus === 'ACTIVE').length}
            </div>
            <div className="text-[11px] text-[#1c1b1f]/60 font-bold uppercase tracking-wider">Active Subscriptions</div>
          </div>
        </div>

        <div className="p-5 bg-[#f9f6f0] border border-black/10 rounded-[28px] shadow-sm flex items-center gap-4">
          <div className="p-3 bg-[#1c1b1f] text-[#ffd64e] rounded-[20px]"><Grid className="w-5 h-5" /></div>
          <div>
            <div className="text-2xl font-black text-[#1c1b1f] font-mono">30 / 30</div>
            <div className="text-[11px] text-[#1c1b1f]/60 font-bold uppercase tracking-wider">Authorized Modules</div>
          </div>
        </div>

        <div className="p-5 bg-[#f9f6f0] border border-black/10 rounded-[28px] shadow-sm flex items-center gap-4">
          <div className="p-3 bg-[#1c1b1f] text-[#ffd64e] rounded-[20px]"><DollarSign className="w-5 h-5" /></div>
          <div>
            <div className="text-2xl font-black text-[#1c1b1f] font-mono">$48,500</div>
            <div className="text-[11px] text-[#1c1b1f]/60 font-bold uppercase tracking-wider">MRR Revenue</div>
          </div>
        </div>
      </div>

      {/* TAB 1: TENANT LICENSING MATRIX */}
      {activeTab === 'licensing' && (
        <div className="bg-[#f9f6f0] rounded-[32px] border border-black/10 shadow-sm overflow-hidden space-y-6 p-6 sm:p-8">
          <div className="flex items-center justify-between border-b border-black/10 pb-4">
            <div>
              <h3 className="text-xl font-black text-[#1c1b1f]">Tenant Administrator Accounts & Subscription Licensing</h3>
              <p className="text-xs text-[#1c1b1f]/70 mt-0.5">Grant active subscriptions and toggle individual module authorizations per tenant</p>
            </div>
          </div>

          <div className="space-y-6">
            {tenantAdmins.map((tenant) => (
              <div key={tenant.id} className="p-6 border border-black/10 rounded-[28px] bg-white space-y-5 shadow-sm">
                
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-black/10 pb-4">
                  <div>
                    <div className="flex items-center gap-2.5">
                      <h4 className="font-black text-[#1c1b1f] text-base">{tenant.schoolName}</h4>
                      <span className="text-[10px] font-mono font-bold px-3 py-1 bg-[#eae7e1] text-[#1c1b1f] rounded-full">
                        subdomain: {tenant.slug}.abilityos.com
                      </span>
                    </div>
                    <div className="text-xs text-[#1c1b1f]/70 mt-1">
                      Admin: <span className="font-extrabold text-[#1c1b1f]">{tenant.adminName}</span> ({tenant.adminEmail}) • Plan: <span className="font-bold text-[#1c1b1f] px-2 py-0.5 bg-[#ffd64e]/20 rounded-md">{tenant.plan}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => grantAllModulesToAdmin(tenant.id)}
                      className="px-4 py-2 bg-[#1c1b1f] hover:bg-black text-[#ffd64e] border border-[#ffd64e]/30 rounded-full text-xs font-black shadow-sm transition"
                    >
                      Grant All 30 Modules
                    </button>

                    <button
                      onClick={() => toggleSubscriptionStatus(tenant.id)}
                      className={`px-4 py-2 rounded-full text-xs font-black shadow-sm transition ${
                        tenant.subscriptionStatus === 'ACTIVE'
                          ? 'bg-[#eae7e1] hover:bg-red-50 text-red-700 border border-red-200'
                          : 'bg-[#ffd64e] hover:bg-[#ffcf33] text-[#1c1b1f]'
                      }`}
                    >
                      {tenant.subscriptionStatus === 'ACTIVE' ? 'Revoke Subscription' : 'Grant Valid Subscription'}
                    </button>
                  </div>
                </div>

                {/* 30 Module Authorizations for this Tenant */}
                <div>
                  <div className="flex items-center justify-between text-[11px] font-black text-[#1c1b1f]/70 uppercase tracking-wider mb-2.5">
                    <span>Enabled Modules ({tenant.modules.length} of 30 Authorized):</span>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {ALL_30_MODULES.map((m) => {
                      const isEnabled = tenant.modules.includes(m.key);
                      return (
                        <button
                          key={m.key}
                          onClick={() => toggleModuleForAdmin(tenant.id, m.key)}
                          className={`px-3 py-2 rounded-xl text-[11px] font-bold transition flex items-center justify-between gap-1.5 ${
                            isEnabled 
                              ? 'bg-[#1c1b1f] text-[#ffd64e] shadow-sm font-black' 
                              : 'bg-[#f9f6f0] text-[#1c1b1f]/50 border border-black/10 hover:border-black/20'
                          }`}
                        >
                          <span className="truncate pr-1">{m.label}</span>
                          {isEnabled ? <Check className="w-3.5 h-3.5 text-[#ffd64e] shrink-0" /> : <X className="w-3 h-3 text-black/30 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 2: DIRECT 30-MODULE SUITE LAUNCHER */}
      {activeTab === 'modules_explorer' && (
        <div className="space-y-4">
          <ModuleExplorer />
        </div>
      )}

      {/* TAB 3: SERVER HEALTH - STRICTLY PHP 8.5 COMPLIANT */}
      {activeTab === 'server_health' && (
        <div className="bg-[#f9f6f0] rounded-[32px] border border-black/10 p-6 sm:p-8 shadow-sm space-y-6">
          <div className="border-b border-black/10 pb-4">
            <h3 className="text-xl font-black text-[#1c1b1f]">cPanel MultiPHP 8.5 & Database Telemetry</h3>
            <p className="text-xs text-[#1c1b1f]/70 mt-0.5">Production WHM runtime configuration complying strictly with PHP 8.5.0 requirement</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 bg-white rounded-[24px] border border-black/10 space-y-1.5 shadow-sm">
              <div className="text-xs font-bold text-[#1c1b1f]/60 uppercase tracking-wider">PHP Version (Enforced)</div>
              <div className="text-lg font-black text-emerald-700 font-mono">PHP 8.5.0 (ea-php85)</div>
              <div className="text-[10px] text-emerald-800 font-bold bg-emerald-50 px-2 py-0.5 rounded-full inline-block border border-emerald-200">
                Production Verified • CLI & FPM
              </div>
            </div>
            <div className="p-5 bg-white rounded-[24px] border border-black/10 space-y-1.5 shadow-sm">
              <div className="text-xs font-bold text-[#1c1b1f]/60 uppercase tracking-wider">MariaDB Storage Engine</div>
              <div className="text-lg font-black text-[#1c1b1f] font-mono">10.11.8-MariaDB (InnoDB)</div>
              <div className="text-[10px] text-[#1c1b1f]/70 font-bold bg-[#eae7e1] px-2 py-0.5 rounded-full inline-block">
                Multi-Tenant Isolation
              </div>
            </div>
            <div className="p-5 bg-white rounded-[24px] border border-black/10 space-y-1.5 shadow-sm">
              <div className="text-xs font-bold text-[#1c1b1f]/60 uppercase tracking-wider">Server Host / SSL</div>
              <div className="text-lg font-black text-[#1c1b1f] font-mono">HTTPS TLS 1.3 Active</div>
              <div className="text-[10px] text-[#1c1b1f]/70 font-bold bg-[#ffd64e]/30 px-2 py-0.5 rounded-full inline-block">
                AutoSSL Certified
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Tenant Admin Modal */}
      {isAddAdminOpen && (
        <div className="fixed inset-0 bg-[#1c1b1f]/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-[#f9f6f0] rounded-[32px] border border-black/10 shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="bg-[#1c1b1f] text-white p-6 flex justify-between items-center">
              <h3 className="text-lg font-black text-white">Add New Tenant Administrator</h3>
              <button onClick={() => setIsAddAdminOpen(false)} className="text-white/60 hover:text-white p-1 rounded-full"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleCreateTenantAdmin} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#1c1b1f] mb-1">School / Clinic Name</label>
                <input
                  type="text" required
                  value={schoolName} onChange={(e) => setSchoolName(e.target.value)}
                  placeholder="e.g. St. Xavier Rehabilitation School"
                  className="w-full px-3.5 py-2.5 bg-white border border-black/15 rounded-xl text-xs font-bold text-[#1c1b1f] focus:ring-2 focus:ring-[#ffd64e] focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-[#1c1b1f] mb-1">Admin Full Name</label>
                  <input
                    type="text" required
                    value={adminName} onChange={(e) => setAdminName(e.target.value)}
                    placeholder="Dr. Sarah Connor"
                    className="w-full px-3.5 py-2.5 bg-white border border-black/15 rounded-xl text-xs font-bold text-[#1c1b1f] focus:ring-2 focus:ring-[#ffd64e] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-[#1c1b1f] mb-1">Admin Email</label>
                  <input
                    type="email" required
                    value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="sarah@stxavier.edu"
                    className="w-full px-3.5 py-2.5 bg-white border border-black/15 rounded-xl text-xs font-bold text-[#1c1b1f] focus:ring-2 focus:ring-[#ffd64e] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#1c1b1f] mb-1">Subscription Plan</label>
                <select
                  value={plan} onChange={(e) => setPlan(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white border border-black/15 rounded-xl text-xs font-bold text-[#1c1b1f] focus:ring-2 focus:ring-[#ffd64e]"
                >
                  <option value="ENTERPRISE_FULL_SUITE">Enterprise Full Suite ($499/mo)</option>
                  <option value="PRO_CLINIC">Pro Rehabilitation Clinic ($299/mo)</option>
                  <option value="GOVT_REHAB">Government Rehab Department ($999/mo)</option>
                </select>
              </div>

              <div className="pt-3 border-t border-black/10 flex justify-end gap-3">
                <button type="button" onClick={() => setIsAddAdminOpen(false)} className="px-5 py-2.5 bg-[#eae7e1] text-[#1c1b1f] font-black rounded-full text-xs hover:bg-[#e0ddd7] transition">Cancel</button>
                <button type="submit" className="px-6 py-2.5 bg-[#ffd64e] hover:bg-[#ffcf33] text-[#1c1b1f] font-black rounded-full text-xs shadow-md transition">Provision Admin & Activate</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
